/* eslint-disable max-lines */
import { GetRowIdFunc, GridApi, GridReadyEvent, IRowNode } from '@ag-grid-community/core';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import dayjs from 'dayjs';
import { isEqual } from 'lodash';
import { BehaviorSubject, combineLatest, firstValueFrom } from 'rxjs';
import { distinctUntilChanged, filter, map, skip, switchMap } from 'rxjs/operators';

import { EdcStatusBannerComponent } from '@widgets/edc-status-banner/edc-status-banner.component';

import { ExportFullDatasetButtonComponent } from '@features/export-full-dataset-button/export-full-dataset-button.component';
import { ProgressTrackerComponent } from '@features/progress-tracker/progress-tracker.component';
import { fetchInProgressEvents } from '@features/progress-tracker/utils/fetch-in-progress-events';
import { WorkflowPanelComponent } from '@features/workflow-panel/workflow-panel.component';

import { SitesQuery } from '@models/sites/sites.query';

import { ButtonComponent } from '@shared/components/button/button.component';
import {
  AuxButtonGroup,
  ButtonGroupComponent,
} from '@shared/components/button-group/button-group.component';
import { File } from '@shared/components/file-manager/state/file.model';
import { PaginationGridComponent } from '@shared/components/pagination-grid/pagination-grid.component';
import { ProcessingLoaderComponent } from '@shared/components/processing-loader/processing-loader.component';
import { LocalStorageKey } from '@shared/constants/localStorageKey';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import { DatasourceService } from '@shared/services/datasource.service';
import {
  batchCreateInvestigatorTransactionsMutation,
  EntityType,
  EventType,
  InvestigatorTransaction,
  InvestigatorTransactionSourceType,
  PermissionType,
  WorkflowStep,
} from '@shared/services/gql.service';
import {
  InvestigatorTransactionsDataRow,
  TransactionMode,
} from '@shared/services/investigator-transactions/investigator-transaction-form.model';
import { InvestigatorTransactionsService } from '@shared/services/investigator-transactions/investigator-transactions.service';
import { LaunchDarklyService } from '@shared/services/launch-darkly.service';
import { OverlayService } from '@shared/services/overlay.service';
import { AuthService } from '@shared/store/auth/auth.service';
import { EventQuery } from '@shared/store/event/event.query';
import { EventService } from '@shared/store/event/event.service';
import { MainQuery } from '@shared/store/main/main.query';
import { WorkflowQuery } from '@shared/store/workflow/workflow.query';
import {
  AgSetColumnsVisible,
  ServerSideColumnFilterType,
  ServerSideFilterInfo,
  ServerSideSortOrder,
} from '@shared/utils';
import { DataStreamUtils } from '@shared/utils/datastream.utils';
import { ExportType } from '@shared/utils/utils';

import { BulkUploadModalComponent } from './components/bulk-upload-modal/bulk-upload-modal.component';
import { ImportPatientDataModalComponent } from './components/import-patient-data-modal/import-patient-data-modal.component';
import { InvestigatorTransactionsFiltersComponent } from './components/investigator-transactions-filters/investigator-transactions-filters.component';
import { InvestigatorTransactionsHeaderComponent } from './components/investigator-transactions-header/investigator-transactions-header.component';
import { InvestigatorTransactionsSaveModalComponent } from './components/investigator-transactions-save-modal/investigator-transactions-save-modal.component';
import { RemoveTransactionModalComponent } from './components/remove-transaction-modal/remove-transaction-modal.components';
import {
  CATEGORY_OPTIONS,
  getInvestigatorTransactionCols,
} from './constants/investigator-transactions-grid-cols';
import { InvestigatorTransactionsPageService } from './services/investigator-transactions-page.service';
import { InvestigatorTransactionTableFormService } from './services/investigator-transactions-table-form.service';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    EdcStatusBannerComponent,
    WorkflowPanelComponent,
    ProcessingLoaderComponent,
    InvestigatorTransactionsHeaderComponent,
    ProgressTrackerComponent,
    InvestigatorTransactionsFiltersComponent,
    FormsModule,
    ReactiveFormsModule,
    ButtonComponent,
    ButtonGroupComponent,
    TooltipDirective,
    PaginationGridComponent,
    ExportFullDatasetButtonComponent,
    AsyncPipe,
  ],
  selector: 'aux-investigator-transactions',
  templateUrl: './investigator-transactions.component.html',
})
export class InvestigatorTransactionsComponent implements OnInit {
  investigatorTransactionsPageService = inject(InvestigatorTransactionsPageService);
  siteQuery = inject(SitesQuery);
  workflowQuery = inject(WorkflowQuery);

  private authService = inject(AuthService);
  private datasourceService = inject(DatasourceService);
  private destroyRef = inject(DestroyRef);
  private eventQuery = inject(EventQuery);
  private eventService = inject(EventService);
  private formBuilder = inject(FormBuilder);
  private investigatorTransactionsService = inject(InvestigatorTransactionsService);
  private launchDarklyService = inject(LaunchDarklyService);
  private mainQuery = inject(MainQuery);
  private overlayService = inject(OverlayService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  showExportFull = signal(false);
  submitInProgress = signal(false);
  totalRecords = signal(0);

  updateTransactionPermission = this.authService.$isAuthorized({
    permissions: [PermissionType.PERMISSION_ADD_TRANSACTION],
    sysAdminsOnly: false,
  });

  updateTransactionTooltip = computed(() =>
    !this.updateTransactionPermission() ? MessagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION : ''
  );

  sysAdminOnly = this.authService.$isAuthorized({
    sysAdminsOnly: true,
  });
  protocolVersionOptions$ = this.investigatorTransactionsService.protocolVersionOptions$;

  private tableFormInstance!: InvestigatorTransactionTableFormService;

  addTransaction = () => {
    this.goToFistPage();
    this.toggleEditMode('create');

    const latestProtocolVersion = this.protocolVersionOptions$
      .getValue()
      .find(({ is_current }) => is_current);

    this.tableFormInstance.addEntity(latestProtocolVersion);

    this.checkAvailabilityAddNewEntity();
  };

  actionGroupButtons = computed<AuxButtonGroup[]>(() => [
    {
      disabled: !this.updateTransactionPermission(),
      disabledTooltip: this.updateTransactionTooltip(),
      name: 'Single Transaction',
      onClick: () => this.addTransaction(),
    },
    {
      disabled: !this.updateTransactionPermission(),
      disabledTooltip: this.updateTransactionTooltip(),
      name: 'Bulk Transactions',
      onClick: () => this.openBulkTransactionModal(),
    },
    {
      disabled: !this.sysAdminOnly(),
      disabledTooltip: !this.sysAdminOnly()
        ? MessagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION
        : '',
      name: 'Import EDC Data',
      onClick: () => this.openImportPatientDataModal(),
      show: this.launchDarklyService.$flags().import_edc_data,
    },
  ]);

  isPatientsFinalized = this.workflowQuery.getLockStatusByWorkflowStepType(
    WorkflowStep.WF_STEP_MONTH_CLOSE_LOCK_PATIENT_TRACKER
  );

  patientsFinalizedText = computed(() =>
    this.isPatientsFinalized() ? MessagesConstants.PATIENT_TRACKER_CLOSED : ''
  );

  editMode$ = new BehaviorSubject<TransactionMode>(null);
  isContractSelected$ = new BehaviorSubject(false);
  isSiteColumnCollapsed$ = new BehaviorSubject(true);
  patientGroupOptions$ = this.investigatorTransactionsService.patientGroupOptions$;
  savedTransactionIds$ = new BehaviorSubject<string[]>([]);
  showSiteBudgetVersionColumn$ = this.launchDarklyService.select$(
    (flags) => flags.site_budget_version_column
  );
  showSourceColumn$ = this.launchDarklyService.select$(
    (flags) => flags.investigator_transactions_source_column
  );
  siteOptions = this.siteQuery.selectSiteOptions();

  private removeRow = async (rowId: string, createMode: boolean) => {
    const modal = this.overlayService.openPopup<
      { createMode: boolean },
      { documents: File[]; note: string; remove: true },
      RemoveTransactionModalComponent
    >({
      content: RemoveTransactionModalComponent,
      data: { createMode },
      settings: {
        header: 'Delete row',
        primaryButton: {
          action: (instance) => instance?.deleteTransaction(),
          disabled: (instance) =>
            !!(!instance?.ref.data?.createMode && instance?.noteFormControl.invalid),
          label: 'Delete',
          variant: 'negative',
        },
      },
    });

    const response = await firstValueFrom(modal.afterClosed$);

    if (createMode && response.data?.remove) {
      this.tableFormInstance.removeTableRow(rowId);

      return;
    }

    if (response.data) {
      const { success } = await this.investigatorTransactionsService.removeTransaction(
        rowId,
        response.data.note,
        response.data.documents.map(({ bucket_key }) => bucket_key)
      );

      if (success) {
        this.tableFormInstance.removeRow(rowId);
        this.datasource.forceRefresh();
        await this.fetchFilters();
      }
    }
  };

  gridOptions$ = combineLatest([
    this.isContractSelected$,
    this.launchDarklyService.select$((flags) => flags.investigator_transactions_edc_created_date),
    this.showSourceColumn$,
    this.showSiteBudgetVersionColumn$,
    this.savedTransactionIds$,
  ]).pipe(
    map(([isSelected, isEDCColumnBeShown, showSourceColumn, showSiteBudgetVersionColumn]) => {
      return getInvestigatorTransactionCols({
        editRow: (rowId: string) => {
          this.toggleEditMode('edit');

          this.tableFormInstance.editRow(rowId, {
            categoryOptions: CATEGORY_OPTIONS,
            patientGroups: this.patientGroupOptions$.getValue(),
            protocolVersionOptions: this.protocolVersionOptions$.getValue(),
            siteOptions: this.siteOptions(),
          });
        },
        isContractCurrency: isSelected,
        isEDCColumnBeShown,
        isPatientsFinalized: this.isPatientsFinalized,
        isSiteColumnCollapsed$: this.isSiteColumnCollapsed$,
        patientGroups$: this.patientGroupOptions$,
        protocolVersionOptions$: this.protocolVersionOptions$,
        removeRow: this.removeRow,
        savedTransactionIds$: this.savedTransactionIds$,
        showSiteBudgetVersionColumn,
        showSourceColumn,
        siteOptions: this.siteOptions,
        transactionMode$: this.editMode$,
        updatePatientIdField: (rowId: string, category?: string) =>
          this.tableFormInstance.updateValidationByCategory(rowId, category || ''),
        updateSiteFields: (rowId: string, id?: string) => {
          this.tableFormInstance.updateSiteFields(
            rowId,
            id || '',
            this.investigatorTransactionsService.siteMap,
            this.investigatorTransactionsService.getSiteAddress.bind(
              this.investigatorTransactionsService
            ),
            this.investigatorTransactionsService.getPrincipalInvestigatorName.bind(
              this.investigatorTransactionsService
            )
          );
        },
        updateTransactionPermission: this.updateTransactionPermission,
      });
    })
  );

  serverSideFilters: ServerSideFilterInfo<InvestigatorTransaction>[] = [
    {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      column: 'activity_date',
      inputPropertyName: 'start_activity_date',
      type: ServerSideColumnFilterType.IsGreaterThanOrEqualTo,
    },
    {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      column: 'activity_date',
      inputPropertyName: 'end_activity_date',
      type: ServerSideColumnFilterType.IsLessThanOrEqualTo,
    },
    {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      column: 'site_id',
      inputPropertyName: 'site_ids',
      type: ServerSideColumnFilterType.OneOf,
    },
    {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      column: 'patient_group_id',
      inputPropertyName: 'patient_group_ids',
      type: ServerSideColumnFilterType.OneOf,
    },
    {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      column: 'patient_protocol_version_id',
      inputPropertyName: 'patient_protocol_version_ids',
      type: ServerSideColumnFilterType.OneOf,
    },
    {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      column: 'patient_id',
      inputPropertyName: 'patient_ids',
      type: ServerSideColumnFilterType.OneOf,
    },
    {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      column: 'description',
      inputPropertyName: 'descriptions',
      type: ServerSideColumnFilterType.OneOf,
    },
    {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      column: 'country',
      inputPropertyName: 'countries',
      type: ServerSideColumnFilterType.OneOf,
    },
    {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      column: 'patient_protocol_sub_type',
      inputPropertyName: 'patient_protocol_sub_types',
      type: ServerSideColumnFilterType.OneOf,
    },
    {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      column: 'source',
      inputPropertyName: 'sources',
      type: ServerSideColumnFilterType.OneOf,
    },
    {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      column: 'description',
      inputPropertyName: 'patient_protocol_names',
      type: ServerSideColumnFilterType.OneOf,
    },
  ];
  gridFiltersFormGroup = this.formBuilder.group(
    this.serverSideFilters.reduce(
      (acc, curr) => ({
        ...acc,
        [curr.inputPropertyName]: [null],
      }),
      {}
    )
  );

  addButtonGroupOverlayPosition: ConnectedPosition[] = [
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top',
    },
  ];
  addTransactionDisabled$ = new BehaviorSubject(false);
  btnLoading$ = new BehaviorSubject<'export' | false>(false);
  bulkProcessingMessage =
    'Bulk Manual Investigator Transactions are processing. This may take a minute. You can navigate away from this page. You will be notified when complete.';
  bulkUploadProcessing = this.mainQuery.$selectProcessingEvent(
    EventType.MANUAL_INVESTIGATOR_TRANSACTIONS_TEMPLATE_UPLOADED
  );
  readonly datasource = this.datasourceService.transactionDatasource;
  filterLoading$ = new BehaviorSubject(true);
  filterValues$ = new BehaviorSubject<Record<string, unknown>>({});
  getRowId?: GetRowIdFunc = undefined;
  gridAPI!: GridApi;
  isCloseMonthsProcessing$ = this.eventQuery.selectProcessingEvent$(EventType.CLOSE_TRIAL_MONTH);
  isClosingPanelEnabled$ = this.launchDarklyService.select$(
    (flags) => flags.closing_checklist_toolbar
  );
  isQuarterCloseEnabled$ = this.workflowQuery.isWorkflowAvailable$;
  lastSourceRefreshDate$ = new BehaviorSubject('');
  readonly paginationKey = LocalStorageKey.PAGINATION_INVESTIGATOR_TRANSACTIONS;
  progressTrackerId = fetchInProgressEvents(EventType.BULK_EDC_TEMPLATE_UPLOADED, this.destroyRef);
  showBottomTotals$ = new BehaviorSubject(false);
  sortModel$ = new BehaviorSubject<ServerSideSortOrder<InvestigatorTransaction>[]>([]);
  totalCost$ = new BehaviorSubject(0);
  totalRecords$ = new BehaviorSubject(0);
  transactionForm = new FormGroup({
    table: new FormArray([]),
  });
  userHasLockPatientDataPermission = false;
  visitCostsEnabled$ = new BehaviorSubject(false);
  workflow = this.workflowQuery.getWorkflowByStepType(
    WorkflowStep.WF_STEP_MONTH_CLOSE_LOCK_PATIENT_TRACKER
  );
  readonly workflowName = WorkflowStep.WF_STEP_MONTH_CLOSE_LOCK_PATIENT_TRACKER;

  ngOnInit() {
    this.launchDarklyService
      .select$((flags) => flags.visit_costs)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((enabled) => {
        this.visitCostsEnabled$.next(enabled);

        if (this.tableFormInstance) {
          this.tableFormInstance.refreshAllFormRows();
        }
      });

    [
      EventType.UPDATE_INVESTIGATOR_TRANSACTIONS,
      EventType.MANUAL_INVESTIGATOR_TRANSACTIONS_TEMPLATE_UPLOADED,
    ].forEach((event) => {
      this.eventService
        .select$(event)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.datasource.forceRefresh();
        });
    });

    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const startDate = params['startDate']
        ? dayjs(params['startDate']).startOf('month').format('YYYY-MM-DD')
        : '';
      const endDate = params['endDate']
        ? dayjs(params['endDate']).endOf('month').format('YYYY-MM-DD')
        : '';

      if (startDate || endDate) {
        this.investigatorTransactionsService.addInMonthAdjustmentsFilters(startDate, endDate, []);
        void this.router.navigate([], { queryParams: {}, replaceUrl: true });
      }
    });
  }

  cancelChanges = (softRefresh = false) => {
    if (!softRefresh) {
      this.gridAPI?.refreshServerSide({ purge: true });
    }

    this.toggleEditMode(null);

    this.tableFormInstance.clearForm();

    this.gridFiltersFormGroup.enable();

    this.addTransactionDisabled$.next(false);
  };

  async canDeactivate(): Promise<boolean> {
    if (this.transactionForm.controls.table.controls.length) {
      const result = this.overlayService.openUnsavedChangesConfirmation();
      const event = await firstValueFrom(result.afterClosed$);

      return !!event?.data;
    }

    return true;
  }

  checkAvailabilityAddNewEntity() {
    this.addTransactionDisabled$.next(
      this.tableFormInstance.getTableFormArray().length >= this.gridAPI.paginationGetPageSize()
    );
  }

  constructor() {
    this.setUserPermissions();
    void this.fetchFilters();

    effect(() => {
      this.isPatientsFinalized();
      this.updateTransactionPermission();
      const manualTransactionRows: IRowNode[] = [];

      this.gridAPI?.forEachNode((node) => {
        if (
          node?.data?.source_type ===
          InvestigatorTransactionSourceType.INVESTIGATOR_TRANSACTION_SOURCE_MANUAL
        ) {
          manualTransactionRows.push(node);
        }
      });

      this.gridAPI?.redrawRows({
        rowNodes: manualTransactionRows,
      });
    });

    this.savedTransactionIds$
      .pipe(takeUntilDestroyed(this.destroyRef), distinctUntilChanged(isEqual))
      .subscribe((savedTransactionIds) => {
        this.datasource.updateOptions({
          overrideFilter: {
            sort_to_top_ids: savedTransactionIds,
          },
        });
      });

    this.getRowId = (params) => params.data.id;
  }

  private async fetchFilters() {
    this.toggleLoaders(true);

    await firstValueFrom(this.investigatorTransactionsPageService.getTransactionFilters());

    this.toggleLoaders(false);
  }

  getGridContext = () => {
    return {
      formGroup: this.transactionForm,
    };
  };

  goToFistPage() {
    if (this.gridAPI.paginationGetCurrentPage() !== 0) {
      this.gridAPI?.paginationGoToFirstPage();
    }
  }

  onExportInvestigatorTransactions = (trialName: string, dateStr: string) => {
    return firstValueFrom(
      this.eventService.processEvent$({
        entity_id: this.mainQuery.getSelectedTrial()?.id || '',
        entity_type: EntityType.TRIAL,
        payload: JSON.stringify({
          export_type: ExportType.INVESTIGATOR_TRANSACTIONS,
          filename: `${trialName}_auxilius-investigator-transactions__${dateStr}`,
          filter_model: this.datasource.currentServerInput?.filter_model || [],
          sort_model: this.datasource.currentServerInput?.sort_model || [],
        }),
        type: EventType.GENERATE_EXPORT,
      })
    );
  };

  onGridReady({ api }: GridReadyEvent) {
    this.gridAPI = api;

    this.datasource.initialize({
      filters: this.serverSideFilters,
      filterValues$: this.filterValues$,
      parseFunction: (items) =>
        this.investigatorTransactionsService.getPatientTransactionsGridData(items),
      sortModel$: this.sortModel$,
      untilDestroyedPipeOperator: takeUntilDestroyed(this.destroyRef),
    });

    // Default sorting
    this.gridAPI.applyColumnState({
      state: [{ colId: 'activity_date', sort: 'desc', sortIndex: 1 }],
    });

    this.datasource.loading$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((value) => !value),
        skip(1) // Skip initial loading state
      )
      .subscribe(() => {
        this.tableFormInstance.reinitializeTableRows();
      });

    this.datasource.refresh$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .pipe(
        switchMap(() =>
          this.datasourceService.retrieveTransactionAnalytics({
            filter_model: this.datasource.currentServerInput?.filter_model || {},
          })
        )
      )
      .subscribe(({ aggregation, total_rows }) => {
        this.totalCost$.next(aggregation?.total_cost || 0);
        this.totalRecords.set(isNaN(Number(total_rows)) ? 0 : Number(total_rows));
        this.showExportFull.set(DataStreamUtils.showExportFullDataset(Number(total_rows)));

        this.lastSourceRefreshDate$.next(aggregation?.latest_source_date || '');
      });

    this.tableFormInstance = new InvestigatorTransactionTableFormService(
      this.transactionForm as FormGroup,
      api,
      this.visitCostsEnabled$
    );

    combineLatest([
      this.launchDarklyService.select$((flags) => flags.investigator_transactions_site_address),
      this.isSiteColumnCollapsed$,
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([isSiteAddressEnabled, isSiteColumnCollapsed]) => {
        if (!isSiteColumnCollapsed) {
          AgSetColumnsVisible({
            gridApi: this.gridAPI,
            keys: ['site_address'],
            visible: isSiteAddressEnabled,
          });
        }
      });
  }

  onTrackerIdChanged(trackerId: string) {
    this.progressTrackerId.set(trackerId);

    if (!trackerId) {
      this.datasource.forceRefresh();
    }
  }

  private openBulkTransactionModal() {
    this.overlayService.openPopup<undefined, undefined, BulkUploadModalComponent>({
      content: BulkUploadModalComponent,
      settings: {
        header: 'Upload Bulk Transactions',
        primaryButton: {
          action: (instance) => instance?.uploadTemplate(),
          disabled: (instance) => !!instance?.uploadDisabled(),
          label: 'Upload',
        },
      },
    });
  }

  private openImportPatientDataModal() {
    this.overlayService.openPopup<
      { onSuccess: (id: string) => void },
      unknown,
      ImportPatientDataModalComponent
    >({
      content: ImportPatientDataModalComponent,
      data: {
        onSuccess: (id: string) => {
          this.progressTrackerId.set(id);
        },
      },
      settings: {
        header: 'Upload Patient Data',
        primaryButton: {
          action: (instance) => instance?.onUpload(),
          label: 'Upload',
        },
      },
    });
  }

  paginationChange() {
    this.checkAvailabilityAddNewEntity();
  }

  saveTransactions = async () => {
    this.tableFormInstance.markAllFieldsAsTouched();

    if (this.transactionForm.invalid) {
      const errorMessage = this.tableFormInstance.getTableErrorMessage();

      this.overlayService.error(errorMessage);

      return;
    }

    const resp = await firstValueFrom(
      this.overlayService.openPopup<
        undefined,
        { documents: File[]; note: string },
        InvestigatorTransactionsSaveModalComponent
      >({
        content: InvestigatorTransactionsSaveModalComponent,
        settings: {
          header: 'Save Manual Transaction',
          primaryButton: {
            action: (instance) => instance?.save(),
            disabled: (instance) =>
              !!(instance?.noteFormControl.invalid || instance?.uploadingFiles()),
          },
        },
      }).afterClosed$
    );

    if (!resp?.data) return;

    this.submitInProgress.set(true);

    const allFormValues: InvestigatorTransactionsDataRow[] =
      this.transactionForm.getRawValue().table;

    const uploadedDocumentBucketPaths = resp.data.documents.map(({ bucket_key }) => bucket_key);

    const transactionsToUpdate = allFormValues.filter(({ mode }) => mode === 'edit');
    const transactionsToSave = allFormValues.filter(({ mode }) => mode === 'create');

    const updatesPromises: Promise<unknown>[] = [];

    if (transactionsToUpdate.length) {
      updatesPromises.push(
        this.investigatorTransactionsService.updateTransactions(
          transactionsToUpdate,
          resp.data.note,
          uploadedDocumentBucketPaths
        )
      );
    }

    if (transactionsToSave.length) {
      updatesPromises.push(
        this.investigatorTransactionsService.saveTransaction(
          transactionsToSave,
          resp.data.note,
          uploadedDocumentBucketPaths
        )
      );
    }

    const res = await Promise.allSettled(updatesPromises);

    this.storeSavedTransactions(res[updatesPromises.length - 1], transactionsToSave.length);
    void this.fetchFilters();
    this.datasource.forceRefresh();

    if (this.editMode$.getValue() === 'create') {
      this.updateSortColumnsForNewTransactions();
    }

    this.cancelChanges(true);

    setTimeout(() => {
      this.submitInProgress.set(false);
      this.overlayService.success('Successfully saved!');
    }, 1000);
  };

  private setUserPermissions(): void {
    combineLatest([
      this.authService.isAuthorized$({
        permissions: [PermissionType.PERMISSION_CHECKLIST_PATIENT_DATA],
        sysAdminsOnly: false,
      }),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([userHasLockPatientDataPermission]) => {
        this.userHasLockPatientDataPermission = userHasLockPatientDataPermission;
      });
  }

  private storeSavedTransactions(
    savedTransactionsResponse: PromiseSettledResult<Awaited<unknown>>,
    transactionsToSaveCount: number
  ) {
    if (transactionsToSaveCount) {
      if (savedTransactionsResponse.status === 'fulfilled') {
        const ids = (
          savedTransactionsResponse.value as GraphqlResponse<
            batchCreateInvestigatorTransactionsMutation[]
          >
        ).data?.map(({ id }) => id);

        if (ids?.length) {
          this.savedTransactionIds$.next([...this.savedTransactionIds$.getValue(), ...ids]);
        }
      }
    }
  }

  toggleEditMode(mode: TransactionMode) {
    const prevEditMode = this.editMode$.getValue();

    this.editMode$.next(mode);

    AgSetColumnsVisible({
      gridApi: this.gridAPI,
      keys: ['total_cost'],
      visible: !mode,
    });

    AgSetColumnsVisible({
      gridApi: this.gridAPI,
      keys: ['exchange_rate', 'contract_curr'],
      visible: this.isContractSelected$.getValue() && !mode,
    });

    if (!this.isContractSelected$.getValue()) {
      AgSetColumnsVisible({
        gridApi: this.gridAPI,
        keys: ['contract_amount'],
        visible: !!mode,
      });
    }

    if (mode) {
      this.gridFiltersFormGroup.disable();
    }

    if (this.editMode$.getValue() !== prevEditMode) {
      this.gridAPI.redrawRows();
    }
  }

  private toggleLoaders(isLoading: boolean) {
    this.filterLoading$.next(isLoading);
  }

  private updateSortColumnsForNewTransactions() {
    const appliedSortColumns = this.gridAPI
      .getColumnState()
      .filter(({ sort }) => !!sort)
      .map(({ colId }) => colId);

    this.gridAPI.applyColumnState({
      state: [
        ...appliedSortColumns.map((colId) => ({
          colId,
          sort: null,
        })),
        { colId: 'sort_to_top_ids', sort: 'desc', sortIndex: 1 },
        { colId: 'create_date', sort: 'desc', sortIndex: 2 },
      ],
    });
  }
}
